import { forwardRef, useEffect, useRef, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { currencyFormatter as currencyFormatterJs } from '../QuotePage/content/QuoteGrids/utils';
import { formatAddress } from '../../utils/getFormattedAddress';
import { usePolicyPageContext as usePolicyPageContextJs } from './context/PolicyPageContext';
import { ActionButton, ActionRow, ActionSpacer, Grid, IncludeInFormsCheckbox, type Row } from './tabParts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuoteGrids/utils.js formats an AG Grid value
const currencyFormatter = currencyFormatterJs as any as (args: { field: unknown; withDecimals?: boolean }) => string;
type PolicyPageState = { endorsements?: Row[]; setEndorsements: (endorsements: Row[]) => void };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: AG Grid params and API
type GridParams = any;

const FILL_HANDLE_CELL_SELECTION = { handle: { mode: 'fill' } };

/**
 * The Inventory Value Endorsement: for the policy or one of its executed MCEs, which locations
 * appear on the bank's declarations page and at what value; save it into the forms, remove it, or
 * download the declarations with it.
 */
export const InventoryValue = forwardRef<unknown, { policy: Row; setGridReference: (ref: unknown) => void; MCEsData?: Row[] }>(
  ({ policy, setGridReference, MCEsData }) => {
    const { endorsements = [], setEndorsements } = usePolicyPageContext();
    const { createEndorsement, downloadPolicyWithEndorsements, removeEndorsementForm, getEndorsements } = useApi();
    const [inventoryValues, setInventoryValues] = useState<Row[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [selectedSource, setSelectedSource] = useState<string>('policy');
    const [, setSelectAll] = useState(false);
    const gridRef = useRef<GridParams>(null);
    const isProgrammaticSelectionRef = useRef(false);
    const [isGridReady, setIsGridReady] = useState(false);
    const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
    const [include, setInclude] = useState(false);
    const [ordinal, setOrdinal] = useState<unknown>();

    const handleGridReady = (params: GridParams) => {
      gridRef.current = params;
      setIsGridReady(true);
      setGridReference(params);
    };

    const isInvFor = (e: Row, type: 'policy' | 'mce', id: unknown) => {
      if (e?.endorsementType !== 'invEndorsement') return false;
      const parent = e?.endorsementData?.parent;
      return parent?.type === type && String(parent?.id) === String(id);
    };

    const getDropdownOptions = () => {
      const hasPolicyIVE = endorsements?.some((e) => isInvFor(e, 'policy', policy.id));
      const options = [{ value: 'policy', label: hasPolicyIVE ? 'Policy (IVE)' : 'Policy' }];
      const executedMCEs = MCEsData?.filter((mce) => ['executed', 'released'].includes(mce.status)) || [];
      const sortedMCEs = [...executedMCEs].sort(
        (a, b) => Number(new Date(a.effectiveAt || a.effective_at || 0)) - Number(new Date(b.effectiveAt || b.effective_at || 0))
      );
      sortedMCEs.forEach((mce) => {
        const effectiveAt = mce.effectiveAt || mce.effective_at;
        let label = mce.mceNumber || '';
        if (effectiveAt) {
          const date = new Date(effectiveAt);
          if (!isNaN(date.getTime())) {
            label = `${label} - ${date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}`;
          }
        }
        if (endorsements?.some((e) => isInvFor(e, 'mce', mce.id))) {
          label = `${label} (IVE)`;
        }
        options.push({ value: mce.id, label });
      });
      return options;
    };

    const isForSource = (endorsement: Row) => {
      const parent = endorsement?.endorsementData?.parent;
      return selectedSource === 'policy'
        ? parent?.type === 'policy' && parent?.id === policy.id
        : parent?.type === 'mce' && parent?.id === selectedSource;
    };

    const getDeclarations = () => {
      const declarationsValues: Record<string, unknown> = {};
      endorsements
        .filter((e) => e?.endorsementType === 'invEndorsement')
        .filter(isForSource)
        .forEach((endorsement) => {
          Object.entries(endorsement?.endorsementData?.invValueForm || {}).forEach(([locationId, value]) => {
            declarationsValues[locationId] = value;
          });
        });
      return declarationsValues;
    };

    const policyForm = endorsements.find((e) => e.endorsementType === 'PolicyForm' && e.notes === "DEALER'S OPEN LOT DECLARATIONS");

    const getExistingEndorsement = () =>
      endorsements
        .filter((e) => e?.endorsementType === 'invEndorsement')
        .find((endorsement) => {
          const parent = endorsement?.endorsementData?.parent;
          return selectedSource === 'policy'
            ? parent?.type === 'policy' && String(parent?.id) === String(policy.id)
            : parent?.type === 'mce' && String(parent?.id) === String(selectedSource);
        });

    useEffect(() => {
      const existingEndorsement = getExistingEndorsement();
      if (existingEndorsement?.ordinal) {
        setInclude(true);
        setOrdinal(existingEndorsement.ordinal);
      } else {
        setInclude(false);
        setOrdinal(undefined);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSource, policy, endorsements]);

    useEffect(() => {
      const existingDeclarationsValues = getDeclarations();
      const existingInvValueForm = getExistingEndorsement()?.endorsementData?.invValueForm || {};
      const toRow = (location: Row) => ({
        id: location.id,
        locationAddress: location.address,
        riskId: location.riskId,
        locationName: location.name,
        coveredValue: location.coveredValue,
        visibleAddress: location.visibleAddress,
        declarationsColField: existingDeclarationsValues[location.id],
      });

      let newInventoryValues: Row[] = [];
      if (selectedSource === 'policy') {
        newInventoryValues = (policy?.locations || []).map(toRow);
      } else {
        const selectedMCE = MCEsData?.find((mce) => mce.id === selectedSource);
        if (selectedMCE) {
          newInventoryValues = selectedMCE.locations.map(toRow);
        }
      }
      setInventoryValues(newInventoryValues);

      // Pre-select the locations in the existing endorsement's invValueForm, matching ids loosely.
      const preSelectedLocations: Record<string, boolean> = {};
      if (Object.keys(existingInvValueForm).length > 0 && newInventoryValues.length > 0) {
        const locationIdMap = new Map(newInventoryValues.map((loc) => [String(loc.id).toLowerCase().trim(), loc]));
        Object.keys(existingInvValueForm).forEach((locationIdFromEndorsement) => {
          const matchingLocation = locationIdMap.get(String(locationIdFromEndorsement).toLowerCase().trim());
          if (matchingLocation) {
            preSelectedLocations[matchingLocation.id] = true;
          }
        });
      }
      setSelectedLocations(preSelectedLocations);
      setSelectAll(Object.keys(preSelectedLocations).length === newInventoryValues.length && newInventoryValues.length > 0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSource, policy, MCEsData, endorsements]);

    useEffect(() => {
      if (isGridReady && gridRef.current?.api && inventoryValues.length > 0) {
        const selectedRowIds = new Set(Object.keys(selectedLocations).map((id) => String(id).toLowerCase().trim()));
        requestAnimationFrame(() => {
          if (gridRef.current?.api) {
            isProgrammaticSelectionRef.current = true;
            gridRef.current.api.forEachNode((node: GridParams) => {
              if (node.data) {
                node.setSelected(selectedRowIds.has(String(node.data.id).toLowerCase().trim()));
              }
            });
            setTimeout(() => {
              isProgrammaticSelectionRef.current = false;
            }, 100);
          }
        });
      }
    }, [inventoryValues, selectedLocations, isGridReady]);

    const parent = () => ({ type: selectedSource === 'policy' ? 'policy' : 'mce', id: selectedSource === 'policy' ? policy.id : selectedSource });

    const invValueForm = () =>
      Object.entries(selectedLocations)
        .filter(([, selected]) => selected)
        .reduce<Record<string, number>>((acc, [locationId]) => {
          const editedValue = editedValues[locationId];
          const location = inventoryValues.find((loc) => loc.id === locationId);
          if (location) {
            const value = editedValue !== undefined ? editedValue : location.declarationsColField;
            acc[locationId] = parseFloat(value as string) || 0;
          }
          return acc;
        }, {});

    const getSavePayload = () => {
      const payload: Row = { endorsementType: 'invEndorsement', endorsementData: { parent: parent(), invValueForm: invValueForm() } };
      if (ordinal) {
        payload.ordinal = ordinal;
      }
      return payload;
    };

    const handleOnSave = async () => {
      if (include) {
        if (!Object.values(selectedLocations).some((selected) => selected)) {
          notifyError('Please select at least one location before saving.');
          return;
        }
        setIsLoading(true);
        try {
          const response = await createEndorsement(policy.id, getSavePayload());
          try {
            const updatedEndorsements = await getEndorsements(policy.id);
            setEndorsements(updatedEndorsements);
            if (response?.ordinal) {
              setOrdinal(response.ordinal);
            } else {
              const updatedEndorsement = updatedEndorsements.find((e: Row) => {
                if (e?.endorsementType !== 'invEndorsement') return false;
                const p = e?.endorsementData?.parent;
                return selectedSource === 'policy'
                  ? p?.type === 'policy' && String(p?.id) === String(policy.id)
                  : p?.type === 'mce' && String(p?.id) === String(selectedSource);
              });
              if (updatedEndorsement?.ordinal) {
                setOrdinal(updatedEndorsement.ordinal);
              }
            }
          } catch (refreshError) {
            console.error('Error refreshing endorsements:', refreshError);
          }
          if (response?.value) {
            window.open(response.value, '_blank');
          } else {
            notifySuccess('Inventory Value Endorsement saved successfully.');
          }
        } catch (error) {
          console.error('Error saving payload:', error);
          notifyError('An error occurred while saving. Please try again.');
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(true);
        try {
          const existingEndorsement = getExistingEndorsement();
          const removePayload: Row = { endorsementType: 'invEndorsement', endorsementData: { parent: parent() } };
          if (ordinal || existingEndorsement?.ordinal) {
            removePayload.ordinal = ordinal || existingEndorsement?.ordinal;
          }
          await removeEndorsementForm(policy.id, removePayload);
          try {
            setEndorsements(await getEndorsements(policy.id));
          } catch (refreshError) {
            console.error('Error refreshing endorsements:', refreshError);
          }
          notifySuccess('Inventory Value Endorsement removed from policy forms successfully.');
          setOrdinal(undefined);
        } catch (error) {
          console.error('Error removing endorsement:', error);
          notifyError('An error occurred while removing the endorsement. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    const handleOnDownload = async () => {
      if (!Object.values(selectedLocations).some((selected) => selected)) {
        notifyError('Please select at least one location before downloading.');
        return;
      }
      const existingEndorsement = getExistingEndorsement();
      if (!existingEndorsement) {
        notifyError('Please create/save an Inventory Value Endorsement first.');
        return;
      }
      let parentFormEndorsement;
      if (selectedSource === 'policy') {
        parentFormEndorsement = {
          ordinal: policyForm?.ordinal || 1,
          endorsementType: 'PolicyForm',
          formId: policyForm?.formId,
          notes: policyForm?.notes,
        };
      } else {
        const mceEndorsement = endorsements.find((e) => e?.endorsementType === 'MceForm' && e?.endorsementData?.mce_id === selectedSource);
        if (!mceEndorsement) {
          notifyError('MCE form not found.');
          return;
        }
        parentFormEndorsement = {
          ordinal: mceEndorsement?.ordinal || 1,
          endorsementType: 'MceForm',
          formId: mceEndorsement?.formId,
          notes: mceEndorsement?.notes,
          endorsementData: mceEndorsement?.endorsementData,
        };
      }
      const invEndorsementPayload: Row = {
        endorsementType: 'invEndorsement',
        formId: existingEndorsement?.formId,
        endorsementData: { parent: parent(), invValueForm: invValueForm() },
      };
      if (ordinal) {
        invEndorsementPayload.ordinal = ordinal;
      }

      setIsDownloading(true);
      try {
        const response = await downloadPolicyWithEndorsements(policy.id, [parentFormEndorsement, invEndorsementPayload]);
        if (response?.data?.value) {
          window.open(response.data.value, '_blank');
        }
      } catch (error) {
        console.error('Error downloading:', error);
      } finally {
        setIsDownloading(false);
      }
    };

    const columnDefs = [
      {
        headerName: 'RiskId',
        field: 'riskId',
        dataType: 'number',
        editable: false,
        sort: 'asc',
        maxWidth: 110,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      { headerName: 'Name', field: 'locationName', dataType: 'string', editable: false },
      {
        headerName: 'Address',
        field: 'locationAddress',
        dataType: 'string',
        minWidth: 250,
        editable: false,
        valueGetter: (params: GridParams) => {
          const visibleAddress = formatAddress(params.data.visibleAddress) || '';
          const locationAddress = formatAddress(params.data.locationAddress) || '';
          if (visibleAddress === locationAddress) {
            return visibleAddress || locationAddress;
          }
          return visibleAddress && locationAddress ? `${visibleAddress}, ${locationAddress}` : visibleAddress || locationAddress;
        },
      },
      {
        headerName: 'True Covered Value',
        field: 'coveredValue',
        editable: false,
        dataType: 'number',
        valueFormatter: (field: unknown) => currencyFormatter({ field, withDecimals: true }),
      },
      {
        headerName: 'Declarations Page for Bank',
        field: 'declarationsColField',
        dataType: 'number',
        editable: true,
        cellClassRules: { 'ag-cell-editable': () => true },
        valueFormatter: (field: GridParams) => {
          if (field.value == null || field.value === '') return '';
          const numValue = parseFloat(field.value);
          if (isNaN(numValue)) return field.value;
          return currencyFormatter({ field: { ...field, value: numValue }, withDecimals: true });
        },
        valueParser: (params: GridParams) => {
          if (params.newValue == null || params.newValue === '') return null;
          const numValue = parseFloat(String(params.newValue).replace(/[$,]/g, ''));
          return isNaN(numValue) ? null : numValue;
        },
      },
    ];

    const gridOptions = {
      enableCellChangeFlash: true,
      rowSelection: 'multiple',
      onCellValueChanged: (params: GridParams) => {
        if (params.colDef.field === 'declarationsColField') {
          const newValue = params.newValue;
          setInventoryValues((prevValues) =>
            prevValues.map((item) => (item.id === params.data.id ? { ...item, declarationsColField: newValue } : item))
          );
          setEditedValues((prev) => ({ ...prev, [params.data.id]: newValue }));
        }
      },
      onSelectionChanged: (params: GridParams) => {
        if (isProgrammaticSelectionRef.current) {
          return;
        }
        const selectedRows = params.api.getSelectedRows();
        const newSelected: Record<string, boolean> = {};
        selectedRows.forEach((row: Row) => {
          newSelected[row.id] = true;
        });
        setSelectedLocations(newSelected);
        setSelectAll(selectedRows.length === inventoryValues.length);
      },
    };

    const sourceOptions = getDropdownOptions();

    return (
      <div>
        <div className="mt-[1%] ml-2 w-[24%]">
          <Select items={sourceOptions} value={selectedSource} onValueChange={(value) => setSelectedSource(String(value))}>
            <SelectTrigger aria-label="Inventory source" className="mb-4 border-action-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid-container max-h-[600px] min-h-[200px] overflow-y-auto">
          <Grid
            ref={gridRef}
            data={inventoryValues}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            setGridReference={handleGridReady}
            rowSelection="multiple"
            gridOptions={gridOptions}
            floatingFilter={false}
            shouldShowPagination={false}
            cellSelection={FILL_HANDLE_CELL_SELECTION}
          />
        </div>
        <ActionRow>
          <ActionButton onClick={handleOnSave} loading={isLoading}>
            Save
          </ActionButton>
          <ActionButton onClick={handleOnDownload} loading={isDownloading}>
            Download
          </ActionButton>
          <ActionSpacer />
          <IncludeInFormsCheckbox checked={include} onChange={setInclude} />
          <ActionSpacer />
        </ActionRow>
      </div>
    );
  }
);
InventoryValue.displayName = 'InventoryValue';
